import type { SortDirection } from '@kioskgaming/ui/dist/data-table/sortState';
import type { ColumnSortSpec } from '../../../hooks/useTableSort';
import type { AgentBalanceItem, GameProvider, GameWalletStatistics } from '../../../types';

/** Client-side only — aggregated from multiple endpoints on `/wallets`. */
export const GAME_WALLET_OVERVIEW_SORT_COLUMNS: ColumnSortSpec[] = [
  { key: 'provider', label: 'Provider', valueKind: 'text', defaultDirection: 'ASC' },
  { key: 'totalWallets', label: 'Total Wallets', valueKind: 'ranked', defaultDirection: 'DESC' },
  { key: 'inSync', label: 'In-sync Wallets', valueKind: 'ranked', defaultDirection: 'DESC' },
  { key: 'pending', label: 'Pending Wallets', valueKind: 'ranked', defaultDirection: 'DESC' },
  { key: 'failed', label: 'Failed Wallets', valueKind: 'ranked', defaultDirection: 'DESC' },
  { key: 'balance', label: 'Game Agent Balance', valueKind: 'money', defaultDirection: 'DESC' },
  { key: 'lastSynced', label: 'Last Synced', valueKind: 'datetime', defaultDirection: 'DESC' },
  { key: 'health', label: 'Health Status', valueKind: 'ranked', defaultDirection: 'ASC' },
];

export type GameWalletOverviewSortKey =
  (typeof GAME_WALLET_OVERVIEW_SORT_COLUMNS)[number]['key'];

export interface GameWalletOverviewRow {
  provider: GameProvider;
  stats?: GameWalletStatistics;
  statsLoading: boolean;
  healthLoading: boolean;
  agentBalance?: AgentBalanceItem;
  healthLabel: string;
}

export const HEALTH_STATUS_LABEL: Record<string, string> = {
  healthy: 'Healthy',
  out_of_sync: 'Out of sync',
  down_risk: 'Down risk',
  na: 'N/A',
};

const HEALTH_RANK: Record<string, number> = {
  'Down risk': 0,
  'Out of sync': 1,
  Healthy: 2,
  'N/A': 3,
};

export function deriveHealthLabel(item?: AgentBalanceItem): string {
  if (!item?.healthStatus) return 'N/A';
  return HEALTH_STATUS_LABEL[item.healthStatus] ?? 'N/A';
}

export function canShowAgentBalance(healthLabel: string): boolean {
  return healthLabel !== 'N/A';
}

export function canSyncAgentBalance(item?: AgentBalanceItem, providerStatus?: string): boolean {
  if (providerStatus && providerStatus !== 'active') return false;
  if (item?.supportsSync === false) return false;
  return true;
}

function compareNullableNumber(
  a: number | null,
  b: number | null,
  direction: SortDirection,
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return direction === 'ASC' ? a - b : b - a;
}

function sortValue(row: GameWalletOverviewRow, key: GameWalletOverviewSortKey): number | string | null {
  switch (key) {
    case 'provider':
      return row.provider.name.toLowerCase();
    case 'totalWallets':
      return row.stats?.totalWallets ?? null;
    case 'inSync':
      return row.stats?.syncStatusCounts?.in_sync ?? null;
    case 'failed':
      return row.stats?.syncStatusCounts?.failed ?? null;
    case 'pending':
      return row.stats?.syncStatusCounts?.pending ?? null;
    case 'balance':
      return canShowAgentBalance(row.healthLabel) ? row.agentBalance?.balance ?? null : null;
    case 'lastSynced':
      return canShowAgentBalance(row.healthLabel) && row.agentBalance?.lastSyncedAt
        ? new Date(row.agentBalance.lastSyncedAt).getTime()
        : null;
    case 'health':
      return HEALTH_RANK[row.healthLabel] ?? null;
    default:
      return null;
  }
}

export function sortGameWalletOverviewRows(
  rows: GameWalletOverviewRow[],
  sortBy: string | null,
  sortOrder: SortDirection | null,
): GameWalletOverviewRow[] {
  if (!sortBy || !sortOrder) return rows;

  const key = sortBy as GameWalletOverviewSortKey;
  const direction = sortOrder;

  return [...rows].sort((a, b) => {
    const va = sortValue(a, key);
    const vb = sortValue(b, key);

    if (typeof va === 'string' && typeof vb === 'string') {
      const cmp = va.localeCompare(vb);
      return direction === 'ASC' ? cmp : -cmp;
    }

    const na = typeof va === 'number' ? va : null;
    const nb = typeof vb === 'number' ? vb : null;
    const cmp = compareNullableNumber(na, nb, direction);
    if (cmp !== 0) return cmp;

    return a.provider.name.localeCompare(b.provider.name);
  });
}
